import { OppButton, OppInput, OppTextarea, OppSelect } from '../components/ui/control';
import { useSharedAuth, useSharedProfile } from '@litopia/shared';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Mail, Truck,
  Trophy,
  Package, MapPin,
  Bookmark,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useCopy } from '../copy';
import {
  useMyStats, useMyCourier, useCourierTasks,
  useMyUpgradeRequest, useRequestUpgrade,
  useApplyCourier, useUpdateTaskStatus,
  useOppSchools, useOppAreas,
} from '../hooks/useOpp';
import ChinaLocation from 'react-china-location';
import locationData from 'china-location/dist/location.json';
import { ProfileSkeleton } from '../components/ui/Skeleton';
import { applyCourierSchema } from '../lib/schemas';
import { toast } from 'sonner';
import { logError } from '../utils/errorHandler';
import { getInitial } from '../utils/userDisplay';
import { getSharedProfileAvatar, getSharedProfileBio, getSharedProfileDisplayName } from '../utils/sharedProfileDisplay';
import { OppProfileHero } from '../components/profile/OppProfileHero';
import { OppProfileTabs } from '../components/profile/OppProfileTabs';
import { OppProfileFooterActions } from '../components/profile/OppProfileFooterActions';
import { OppProfileLettersTab } from '../components/profile/OppProfileLettersTab';
import { OppProfileAchievementsTab } from '../components/profile/OppProfileAchievementsTab';
import { OppProfileCollectionsTab } from '../components/profile/OppProfileCollectionsTab';
import { OppCourierLevelCards } from '../components/profile/OppCourierLevelCards';
import { OppCourierStatsGrid } from '../components/profile/OppCourierStatsGrid';
import { OppCourierQuickActions } from '../components/profile/OppCourierQuickActions';
import { OppCourierRecentTasks } from '../components/profile/OppCourierRecentTasks';
import { OppCourierHeaderCard } from '../components/profile/OppCourierHeaderCard';
import { OppCourierUpgradeCard } from '../components/profile/OppCourierUpgradeCard';
import { SemanticState } from '../components/semantic/SemanticState';
import { SemanticFeedback } from '../components/semantic/SemanticFeedback';

type ProfileTab = 'letters' | 'courier' | 'achievements' | 'collections';

export default function ProfilePage() {
  const { user, loading, signOut } = useSharedAuth();
  const { t } = useCopy();
  const [copied, setCopied] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: ProfileTab = requestedTab === 'courier' || requestedTab === 'achievements' || requestedTab === 'collections'
    ? requestedTab
    : 'letters';
  const setActiveTab = (nextTab: ProfileTab) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('tab', nextTab);
    setSearchParams(nextSearchParams, { replace: true });
  };
  const { data: userStats } = useMyStats(user?.id);
  const { data: sharedProfileData } = useSharedProfile(user?.id);
  const profileData = sharedProfileData;

  if (loading) return <ProfileSkeleton />;
  if (!user) return <Navigate to="/login" />;

  const displayName = getSharedProfileDisplayName(profileData, user.email);
  const avatarUrl = getSharedProfileAvatar(profileData);
  const bio = getSharedProfileBio(profileData);
  const initial = getInitial(displayName);
  const opCode = profileData?.opp_op_code ?? '------';

  const copyOpCode = async () => {
    try {
      await navigator.clipboard.writeText(opCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('common.copyFailed'));
    }
  };

  const userPoints = userStats?.total_points ?? 0;
  const LEVEL_THRESHOLDS = [0, 50, 150, 400, 800, 1500];
  const userLevel = LEVEL_THRESHOLDS.reduceRight((acc, threshold, idx) => acc === -1 && userPoints >= threshold ? idx : acc, -1) + 1;
  const nextThreshold = LEVEL_THRESHOLDS[userLevel] ?? LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  const prevThreshold = LEVEL_THRESHOLDS[userLevel - 1] ?? 0;
  const levelProgress = nextThreshold > prevThreshold
    ? Math.min(100, ((userPoints - prevThreshold) / (nextThreshold - prevThreshold)) * 100)
    : 100;

  const STATS = [
    { label: t('profile.stats.sent'), value: userStats?.letters_sent ?? 0 },
    { label: t('profile.stats.received'), value: userStats?.letters_received ?? 0 },
    { label: t('profile.stats.points'), value: userStats?.total_points ?? 0, highlight: true },
    { label: t('profile.stats.deliveries'), value: userStats?.courier_points ?? 0 },
    { label: t('profile.stats.museum'), value: userStats?.museum_contributions ?? 0 },
  ];

  const TABS: { id: ProfileTab; label: string; icon: React.ElementType }[] = [
    { id: 'letters', label: t('profile.stats.sent'), icon: Mail },
    { id: 'courier', label: t('nav.courier'), icon: Truck },
    { id: 'achievements', label: t('profile.achievements'), icon: Trophy },
    { id: 'collections', label: t('discover.tab.collections'), icon: Bookmark },
  ];

  return (
    <div className="min-h-[var(--litopia-layout-100dvh)] bg-letter-paper">
      <OppProfileHero
        bannerUrl={profileData?.banner_url}
        avatarUrl={avatarUrl}
        displayName={displayName}
        bio={bio}
        initial={initial}
        userLevel={userLevel}
        levelProgress={levelProgress}
        opCode={opCode}
        copied={copied}
        onCopyOpCode={copyOpCode}
      />

      {/* CONTENT BELOW BANNER */}
      <div className="relative mx-auto max-w-[var(--litopia-layout-1400px)] px-4 md:px-8 lg:px-12">
        <OppProfileTabs
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as ProfileTab)}
          stats={STATS}
        />

        {/* TAB CONTENT */}
        <div className="py-6">
          {activeTab === 'letters' && <OppProfileLettersTab sentCount={userStats?.letters_sent ?? 0} />}
          {activeTab === 'courier' && <CourierTabFull onCopyOpCode={copyOpCode} displayName={displayName} />}
          {activeTab === 'achievements' && <OppProfileAchievementsTab />}
          {activeTab === 'collections' && <OppProfileCollectionsTab />}
        </div>

        <OppProfileFooterActions
          settingsLabel={t('nav.settings')}
          signOutLabel={t('common.signout')}
          onSignOut={async () => {
            await signOut();
          }}
        />
      </div>
    </div>
  );
}

type LocationSelection = {
  province?: { code: string; name: string } | null;
  city?: { code: string; name: string } | null;
  district?: { code: string; name: string } | null;
};

type ApplyCourierForm = {
  name: string;
  school_code: string;
  area_code: string;
  transport_method: string;
  weekly_hours: number | undefined;
  self_intro: string;
  phone: string;
  province_code: string;
  province_name: string;
  city_code: string;
  city_name: string;
  district_code: string;
  district_name: string;
};

/** Full courier tab — absorbs all Courier.tsx functionality */
function CourierTabFull({ onCopyOpCode, displayName }: { onCopyOpCode: () => void; displayName: string }) {
  const { t } = useCopy();
  const navigate = useNavigate();
  const { data: courier, isLoading: courierLoading, isError: courierError, refetch: refetchCourier } = useMyCourier();
  const { data: tasks, isLoading: tasksLoading, isError: tasksError, refetch: refetchTasks } = useCourierTasks(courier?.id);
  const { data: existingUpgrade } = useMyUpgradeRequest(courier?.id);
  const requestUpgrade = useRequestUpgrade();
  const applyCourier = useApplyCourier();
  const updateTaskStatus = useUpdateTaskStatus();

  const [applyForm, setApplyForm] = useState<ApplyCourierForm>({
    name: '',
    school_code: '',
    area_code: '',
    transport_method: 'walk',
    weekly_hours: undefined,
    self_intro: '',
    phone: '',
    province_code: '',
    province_name: '',
    city_code: '',
    city_name: '',
    district_code: '',
    district_name: '',
  });
  const [applyFormOpen, setApplyFormOpen] = useState(false);
  const [applySubmitted, setApplySubmitted] = useState(false);
  const [upgradeSubmitted, setUpgradeSubmitted] = useState(false);
  const [courierFeedback, setCourierFeedback] = useState<{ kind: 'success' | 'error'; title: string; message: string } | null>(null);
  const [locationWasSelected, setLocationWasSelected] = useState(false);
  const locationInteractionStarted = useRef(false);

  const { data: schools = [], isError: schoolsError, refetch: refetchSchools } = useOppSchools();
  const { data: areas = [] } = useOppAreas(applyForm.school_code || undefined);

  const handleLocationChange = (nextLocation: LocationSelection) => {
    // react-china-location emits its default Beijing value on mount. Ignore
    // that callback until the user actually interacts with the picker.
    if (!locationInteractionStarted.current) return;
    const provinceCode = nextLocation.province?.code ?? '';
    const provinceName = nextLocation.province?.name ?? '';
    const cityCode = nextLocation.city?.code ?? '';
    const cityName = nextLocation.city?.name ?? '';
    const districtCode = nextLocation.district?.code ?? '';
    const districtName = nextLocation.district?.name ?? '';

    setApplyForm((prev) => ({
      ...prev,
      province_code: provinceCode,
      province_name: provinceName,
      city_code: cityCode,
      city_name: cityName,
      district_code: districtCode,
      district_name: districtName,
    }));
    setLocationWasSelected(Boolean(provinceCode && cityCode && districtCode));
  };

  const LEVELS = [
    { level: 1, title: t('courier.level.1'), desc: t('courier.level.1.desc'), color: 'text-feedback-success', bg: 'bg-feedback-success/10', border: 'border-feedback-success/30', badgeBg: 'bg-feedback-success/20', badgeText: 'text-feedback-success' },
    { level: 2, title: t('courier.level.2'), desc: t('courier.level.2.desc'), color: 'text-feedback-info', bg: 'bg-feedback-info/10', border: 'border-feedback-info/30', badgeBg: 'bg-feedback-info/20', badgeText: 'text-feedback-info' },
    { level: 3, title: t('courier.level.3'), desc: t('courier.level.3.desc'), color: 'text-[hsl(var(--opp-accent-strong))]', bg: 'bg-[hsl(var(--opp-accent-soft))]', border: 'border-[hsl(var(--opp-border))]', badgeBg: 'bg-[hsl(var(--opp-accent-soft))]', badgeText: 'text-[hsl(var(--opp-accent-strong))]' },
    { level: 4, title: t('courier.level.4'), desc: t('courier.level.4.desc'), color: 'text-[hsl(var(--opp-accent-strong))]', bg: 'bg-[hsl(var(--opp-surface-raised))]', border: 'border-[hsl(var(--opp-border))]', badgeBg: 'bg-[hsl(var(--opp-accent-soft))]', badgeText: 'text-[hsl(var(--opp-ink))]' },
  ];

  const TRANSPORT_OPTIONS = [
    { value: 'walk', label: t('courier.form.transport.walk'), icon: '🚶' },
    { value: 'bike', label: t('courier.form.transport.bike'), icon: '🚲' },
    { value: 'ebike', label: t('courier.form.transport.ebike'), icon: '⚡' },
  ];

  const currentLevel = courier ? LEVELS.find((l) => l.level === courier.level) : null;

  if (courierLoading) return <SemanticState compact kind="loading" title="正在加载信使资料" />;
  if (courierError) return <SemanticState compact kind="error" title="信使资料加载失败" description="请检查网络后重试，申请和任务暂时不可操作。" action={<OppButton onClick={() => refetchCourier()} variant="primary" className="min-h-11 rounded-full px-4">重试</OppButton>} />;

  if (courier && courier.status !== 'approved') {
    const isPending = courier.status === 'pending';
    return <SemanticState compact kind="notice" title={isPending ? '信使申请正在审核' : courier.status === 'suspended' ? '信使资格已暂停' : '信使申请未通过'} description={isPending ? '审核通过后，任务和投递操作会出现在这里。你可以在通知中查看结果。' : '当前无法处理投递任务，请在通知中查看说明或联系管理员。'} action={<OppButton onClick={() => navigate('/notifications?filter=courier')} variant="outline" className="min-h-11 rounded-full px-4">查看信使通知</OppButton>} />;
  }

  // Not a courier — show apply flow
  if (!courier) {
    return (
      <div className="space-y-6">
        {/* Hero card */}
        <div className="rounded-2xl bg-letter-amber-light p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--opp-surface-raised))]">
              <Truck className="h-8 w-8 text-[hsl(var(--opp-accent-strong))]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg font-bold text-[hsl(var(--opp-ink))]">{t('courier.apply.title')}</h3>
              <p className="mt-1 text-sm text-[hsl(var(--opp-accent-strong))] leading-relaxed">{t('courier.apply.desc')}</p>
            </div>
          </div>
          {!applyFormOpen && !applySubmitted && (
            <OppButton
              onClick={() => setApplyFormOpen(true)}
              variant="primary"
              className="mt-4 min-h-11 w-full rounded-full px-6 py-2.5 text-sm font-semibold"
            >
              {t('courier.apply.btn')}
            </OppButton>
          )}
          {applySubmitted && (
            <SemanticFeedback kind="success" title={t('courier.form.submitted')} className="mt-4">审核结果会通过通知告知你。</SemanticFeedback>
          )}
        </div>

        {courierFeedback && <SemanticFeedback kind={courierFeedback.kind} title={courierFeedback.title}>{courierFeedback.message}</SemanticFeedback>}

        {/* Apply form */}
        {applyFormOpen && !applySubmitted && (
          <div className="space-y-4">
            <h4 className="font-serif text-base font-bold text-[hsl(var(--opp-ink))]">{t('courier.form.title')}</h4>
            {schoolsError && <SemanticFeedback kind="error" title="学校列表加载失败" action={<OppButton onClick={() => refetchSchools()} variant="outline" className="min-h-11 rounded-full px-3">重试</OppButton>}>暂时无法选择学校，请稍后重试。</SemanticFeedback>}
            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="courier-name" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.name')}</label>
              <OppInput
                id="courier-name"
                type="text" placeholder={t('courier.form.name.placeholder')}
                value={applyForm.name} onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
                className="w-full rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2.5 text-sm text-[hsl(var(--opp-ink))] placeholder:text-[hsl(var(--opp-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)]"
              />
            </div>
            {/* School */}
            <div className="space-y-1.5">
              <label htmlFor="courier-school" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.school')}</label>
              <OppSelect
                id="courier-school"
                value={applyForm.school_code}
                onChange={(e) => setApplyForm({ ...applyForm, school_code: e.target.value, area_code: '' })}
                className="w-full rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2.5 text-sm text-[hsl(var(--opp-ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)]"
              >
                <option value="">{t('courier.form.school.placeholder')}</option>
                {schools.map((s) => (
                  <option key={s.school_code} value={s.school_code}>{s.school_name}</option>
                ))}
              </OppSelect>
            </div>
            <div className="space-y-1.5">
              <span id="courier-location-label" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.location')}</span>
              <div
                role="group"
                aria-labelledby="courier-location-label"
                className="rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2"
                onPointerDownCapture={() => { locationInteractionStarted.current = true; }}
                onKeyDownCapture={() => { locationInteractionStarted.current = true; }}
              >
                <ChinaLocation list={locationData} onLocationChange={handleLocationChange} />
              </div>
            </div>
            {/* Zone */}
            <div className="space-y-1.5">
              <label htmlFor="courier-zone" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.zone')}</label>
              <OppSelect
                id="courier-zone"
                value={applyForm.area_code}
                onChange={(e) => setApplyForm({ ...applyForm, area_code: e.target.value })}
                disabled={!applyForm.school_code || areas.length === 0}
                className="w-full rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2.5 text-sm text-[hsl(var(--opp-ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{t('courier.form.zone.placeholder')}</option>
                {areas.map((a) => (
                  <option key={a.area_code} value={a.area_code}>{a.area_name}</option>
                ))}
              </OppSelect>
            </div>
            {/* Transport */}
            <div className="space-y-1.5">
              <span id="courier-transport-label" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.transport')}</span>
              <div role="group" aria-labelledby="courier-transport-label" className="grid grid-cols-3 gap-2">
                {TRANSPORT_OPTIONS.map((opt) => (
                  <OppButton
                    key={opt.value}
                    onClick={() => setApplyForm({ ...applyForm, transport_method: opt.value })}
                    aria-pressed={applyForm.transport_method === opt.value}
                    className={`min-h-11 rounded-lg border-2 p-3 text-center transition-colors ${
                      applyForm.transport_method === opt.value
                        ? 'border-[hsl(var(--opp-accent))] bg-letter-amber-light'
                        : 'border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))]'
                    }`}
                  >
                    <span className="text-lg">{opt.icon}</span>
                    <p className="mt-1 text-xs font-medium text-[hsl(var(--opp-ink))]">{opt.label}</p>
                  </OppButton>
                ))}
              </div>
            </div>
            {/* Weekly hours */}
            <div className="space-y-1.5">
              <label htmlFor="courier-weekly-hours" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.hours')}</label>
              <OppInput
                id="courier-weekly-hours"
                type="number" placeholder={t('courier.form.hours.placeholder')} min={0}
                value={applyForm.weekly_hours ?? ''} onChange={(e) => setApplyForm({ ...applyForm, weekly_hours: e.target.value === '' ? undefined : Number(e.target.value) })}
                className="w-full rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2.5 text-sm text-[hsl(var(--opp-ink))] placeholder:text-[hsl(var(--opp-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)]"
              />
            </div>
            {/* Self intro */}
            <div className="space-y-1.5">
              <label htmlFor="courier-self-intro" className="text-xs font-semibold text-[hsl(var(--opp-ink))]">{t('courier.form.intro')}</label>
              <OppTextarea
                id="courier-self-intro"
                rows={3} placeholder={t('courier.form.intro.placeholder')}
                value={applyForm.self_intro} onChange={(e) => setApplyForm({ ...applyForm, self_intro: e.target.value })}
                className="w-full rounded-lg border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] px-3 py-2.5 text-sm text-[hsl(var(--opp-ink))] placeholder:text-[hsl(var(--opp-muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--opp-accent)/.20)] resize-none"
              />
            </div>
            <OppButton
              disabled={
                !applyForm.name
                || !applyForm.school_code
                || !locationWasSelected
                || !applyForm.province_code
                || !applyForm.city_code
                || !applyForm.district_code
                || applyCourier.isPending
              }
              onClick={() => {
                const result = applyCourierSchema.safeParse(applyForm);
                if (!result.success) {
                  setCourierFeedback({ kind: 'error', title: '请检查申请信息', message: result.error.errors[0].message });
                  return;
                }
                setCourierFeedback(null);
                applyCourier.mutate(applyForm, {
                  onSuccess: () => { setApplySubmitted(true); setApplyFormOpen(false); },
                  onError: (err) => {
                    logError(err, 'Courier application');
                    setCourierFeedback({ kind: 'error', title: '申请提交失败', message: err instanceof Error ? err.message : t('common.error') });
                  },
                });
              }}
              variant="primary"
              className="min-h-11 w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
            >
              {applyCourier.isPending ? t('courier.form.submitting') : t('courier.form.submit')}
            </OppButton>
          </div>
        )}

        <OppCourierLevelCards levels={LEVELS} />
      </div>
    );
  }

  // Active courier — dashboard
  const today = new Date().toDateString();
  const todayTasks = tasks?.filter((t: { created_at: string }) => new Date(t.created_at).toDateString() === today) ?? [];
  const deliveredTasks = tasks?.filter((t: { status: string }) => t.status === 'delivered') ?? [];
  const activeTasks = tasks?.filter((t: { status: string }) => ['pending', 'collected', 'in_transit'].includes(t.status)) ?? [];

  return (
    <div className="space-y-5">
      <OppCourierHeaderCard
        name={courier.name || displayName}
        level={courier.level}
        title={currentLevel?.title}
        badgeBg={currentLevel?.badgeBg}
        badgeText={currentLevel?.badgeText}
      />

      <OppCourierStatsGrid
        stats={[
          { label: t('courier.stats.today'), value: String(todayTasks.length), bg: currentLevel?.bg, border: currentLevel?.border, textColor: currentLevel?.badgeText, subColor: currentLevel?.color },
          { label: t('courier.stats.completed'), value: String(deliveredTasks.length), bg: currentLevel?.bg, border: currentLevel?.border, textColor: currentLevel?.badgeText, subColor: currentLevel?.color },
          { label: t('courier.stats.pending'), value: String(activeTasks.length), bg: currentLevel?.bg, border: currentLevel?.border, textColor: currentLevel?.badgeText, subColor: currentLevel?.color },
          { label: t('courier.stats.points'), value: String(courier.points ?? 0), bg: 'bg-accent', border: 'border-border', textColor: 'text-foreground', subColor: 'text-muted-foreground' },
        ]}
      />

      <OppCourierQuickActions
        actions={[
          { label: t('courier.actions.tasks'), icon: Package, onClick: () => document.getElementById('courier-tasks')?.scrollIntoView({ behavior: 'smooth' }), badgeBg: currentLevel?.badgeBg, badgeText: currentLevel?.badgeText },
          { label: t('courier.actions.opcode'), icon: MapPin, onClick: onCopyOpCode, badgeBg: currentLevel?.badgeBg, badgeText: currentLevel?.badgeText },
        ]}
      />

      {courierFeedback && <SemanticFeedback kind={courierFeedback.kind} title={courierFeedback.title}>{courierFeedback.message}</SemanticFeedback>}
      {tasksLoading && <SemanticState compact kind="loading" title="正在加载投递任务" />}
      {tasksError && <SemanticState compact kind="error" title="任务加载失败" description="任务状态暂时不可用，请刷新后再操作。" action={<OppButton onClick={() => refetchTasks()} variant="primary" className="min-h-11 rounded-full px-4">重试</OppButton>} />}
      {!tasksLoading && !tasksError && <div id="courier-tasks">
      <OppCourierRecentTasks
        tasks={tasks}
        pendingTaskId={updateTaskStatus.isPending ? updateTaskStatus.variables?.taskId : null}
        onCollect={(taskId) => updateTaskStatus.mutate({ taskId, status: 'collected' }, {
          onSuccess: () => setCourierFeedback({ kind: 'success', title: '已取件', message: '任务状态已更新。' }),
          onError: (err) => setCourierFeedback({ kind: 'error', title: '取件失败', message: err instanceof Error ? err.message : t('common.error') }),
        })}
        onDepart={(taskId) => updateTaskStatus.mutate({ taskId, status: 'in_transit' }, {
          onSuccess: () => setCourierFeedback({ kind: 'success', title: '已出发', message: '任务状态已更新。' }),
          onError: (err) => setCourierFeedback({ kind: 'error', title: '更新失败', message: err instanceof Error ? err.message : t('common.error') }),
        })}
        onDeliver={(taskId) => updateTaskStatus.mutate({ taskId, status: 'delivered' }, {
          onSuccess: () => setCourierFeedback({ kind: 'success', title: '已送达', message: '投递任务已完成。' }),
          onError: (err) => setCourierFeedback({ kind: 'error', title: '送达确认失败', message: err instanceof Error ? err.message : t('common.error') }),
        })}
      />
      </div>}

      <OppCourierUpgradeCard
        visible={courier.level < 4}
        nextLevel={courier.level + 1}
        title={t('courier.upgrade.title')}
        isPending={requestUpgrade.isPending}
        isSubmitted={existingUpgrade?.status === 'pending' || upgradeSubmitted}
        submittedLabel={t('courier.upgrade.submitted')}
        submittingLabel={t('courier.upgrade.submitting')}
        buttonLabel={t('courier.upgrade.btn')}
        onSubmit={() => {
          requestUpgrade.mutate(
            { courierId: courier.id, currentLevel: courier.level, requestLevel: courier.level + 1 },
            {
              onSuccess: () => setUpgradeSubmitted(true),
              onError: (err) => {
                logError(err, 'Upgrade request');
                toast.error(err instanceof Error ? err.message : t('common.error'));
              },
            }
          );
        }}
      />
    </div>
  );
}
